// ─── Billing Service ────────────────────────────────────────────────────────────
// Calls Netlify serverless functions which proxy to Stripe.
// The Stripe secret key never touches the browser — it lives only in
// the Netlify Function environment variables.

import { supabase } from "@/lib/supabase/client";

async function callFunction(path: string, body: object): Promise<string> {
  const { data: { session } } = await supabase.auth.getSession();
  const response = await fetch(path, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "Authorization": `Bearer ${session?.access_token ?? ""}`,
    },
    body: JSON.stringify(body),
  });

  if (!response.ok) {
    const text = await response.text();
    throw new Error(`Billing request failed (${response.status}): ${text}`);
  }

  const data = (await response.json()) as { url?: string };
  if (!data.url) throw new Error("No redirect URL returned from billing service");
  return data.url;
}

export const billingService = {
  /**
   * Create a Stripe Checkout session for a subscription.
   * Returns the Stripe-hosted checkout URL — redirect the user there.
   */
  async createCheckoutSession(
    priceId: string,
    userEmail: string,
    userId: string,
  ): Promise<string> {
    return callFunction("/.netlify/functions/create-checkout-session", {
      priceId,
      userEmail,
      userId,
    });
  },

  /**
   * Create a Stripe Customer Portal session for managing billing.
   * Returns the portal URL — redirect the user there.
   */
  async createPortalSession(stripeCustomerId: string): Promise<string> {
    return callFunction("/.netlify/functions/create-portal-session", {
      stripeCustomerId,
    });
  },
};
